import { environment } from '../../environments/environment';
import { ActiviteAPI } from './activite';
import { Adresse } from './adresse';
import { Assure, AssureAPI } from './assure';
import { ChampLibre } from './champLibre';
import { DetailTelesuivi, DetailTelesuiviAPI } from './detailTelesuivi';
import { PatientComplement, PatientComplementAPI } from './patientComplement';
import { InterventionList, InterventionListAPI } from './intervention';
import { Observance } from './observance';
import {
  Prescription,
  PrescriptionAPI,
  PrescriptionList,
} from './prescription';
import { TableauDeBord, TableauDeBordAPI } from './tableauDeBord';
import { Telesuivi, TelesuiviAPI } from './telesuivi';
import {
  PrescriptionForm,
  PrescriptionFormAPI,
} from './prescriptionForm/prescriptionForm';
import { User, UserAPI } from './user';
import { PpcInformationsComplementaires } from './prescriptionForm/ppcInformationsComplementaires';

export class Patient {
  id: number;
  nom: string;
  prenom: string;
  titre: string;
  sexe?: 'Homme' | 'Femme';
  dateDeNaissance?: Date;
  adresse?: Adresse;
  mobile?: string;
  telephone?: string;
  mail?: string;
  interventionList?: InterventionList;
  tableauDeBord?: TableauDeBord;
  telesuivi?: Telesuivi;
  obs?: ChampLibre;
  iahResiduel?: ChampLibre;
  fuites?: ChampLibre;
  p90_95?: ChampLibre;
  prescriptionList?: PrescriptionList;
  premierePrescriptionNonTransmise?: Prescription;
  dernierePrescription?: Prescription | null;
  nombrePrescriptionEnCoursDeTransmission?: number;
  dernierePrescriptionFormEnCoursDeTransmission?: PrescriptionForm;
  nombrePrescriptionEnAttente?: number;
  nir?: string;
  detailTelesuivi?: DetailTelesuivi;
  activiteList?: string[];
  assure?: Assure;
  medecinList?: User[];
  canSign?: boolean;
  prescriptionFormPrecedente?: PrescriptionForm;
  patientComplement?: PatientComplement;

  constructor(
    id: number,
    nom: string,
    prenom: string,
    titre: string,
    sexe?: 'Homme' | 'Femme',
    dateDeNaissance?: Date,
    adresse?: Adresse,
    mobile?: string,
    telephone?: string,
    mail?: string,
    interventionList?: InterventionList,
    tableauDeBord?: TableauDeBord,
    telesuivi?: Telesuivi,
    prescriptionList?: PrescriptionList,
    premierePrescriptionNonTransmise?: Prescription,
    dernierePrescription?: Prescription,
    nombrePrescriptionEnCoursDeTransmission?: number,
    dernierePrescriptionFormEnCoursDeTransmission?: PrescriptionForm,
    nombrePrescriptionEnAttente?: number,
    nir?: string,
    detailTelesuivi?: DetailTelesuivi,
    activiteList?: string[],
    assure?: Assure,
    medecinList?: User[],
    canSign?: boolean,
    prescriptionFormPrecedente?: PrescriptionForm,
    patientComplement?: PatientComplement,
  ) {
    this.id = id;
    this.nom = nom;
    this.prenom = prenom;
    this.titre = titre;
    this.sexe = sexe;
    this.dateDeNaissance = dateDeNaissance;
    this.adresse = adresse;
    this.mobile = mobile;
    this.telephone = telephone;
    this.mail = mail;
    this.interventionList = interventionList;
    this.tableauDeBord = tableauDeBord;
    this.telesuivi = telesuivi;
    this.prescriptionList = prescriptionList;
    this.premierePrescriptionNonTransmise = premierePrescriptionNonTransmise;
    this.dernierePrescription = dernierePrescription;
    this.nombrePrescriptionEnCoursDeTransmission =
      nombrePrescriptionEnCoursDeTransmission;
    this.dernierePrescriptionFormEnCoursDeTransmission =
      dernierePrescriptionFormEnCoursDeTransmission;
    this.nombrePrescriptionEnAttente = nombrePrescriptionEnAttente;
    this.nir = nir;
    this.detailTelesuivi = detailTelesuivi;
    this.activiteList = activiteList;
    this.assure = assure;
    this.medecinList = medecinList;
    this.canSign = canSign;
    this.prescriptionFormPrecedente = prescriptionFormPrecedente;
    this.patientComplement = patientComplement;

    const dateFin = new Date();
    const dateDebut = new Date();
    dateDebut.setDate(dateFin.getDate() - environment.nombreDeJourPourMoyenne);

    const observanceMoyenne = this.observanceMoyenne(dateDebut, dateFin);

    this.obs = this.calculObs(observanceMoyenne);
    this.iahResiduel = this.calculIahResiduel(observanceMoyenne);
    this.fuites = this.calculFuites(observanceMoyenne);
    this.p90_95 = this.calculP90_95(observanceMoyenne);
  }

  acceptOrdonnance(): boolean {
    return (
      (this.patientComplement?.tl1 &&
        (this.dernierePrescription?.type != 'initiale' ||
          (this.prescriptionFormPrecedente
            ?.informationsComplementaires instanceof
            PpcInformationsComplementaires &&
            this.prescriptionFormPrecedente?.informationsComplementaires
              .ppcTypeSlug != 'dap_initial'))) ??
      false
    );
  }

  static factory(patientAPI: PatientAPI): Patient {
    return new Patient(
      patientAPI.Numero,
      patientAPI.Nom,
      patientAPI.Prenom,
      patientAPI.NomTitre,
      patientAPI.Sexe == 1
        ? 'Homme'
        : patientAPI.Sexe == 2
          ? 'Femme'
          : undefined,
      patientAPI.DateNaissance ? new Date(patientAPI.DateNaissance) : undefined,
      patientAPI.Adresse1
        ? new Adresse(
            patientAPI.Adresse1,
            patientAPI.Adresse2,
            patientAPI.Adresse3,
            patientAPI.CodePostal,
            patientAPI.Ville,
            patientAPI.Pays,
          )
        : undefined,
      patientAPI.Mobile,
      patientAPI.Telephone,
      patientAPI.Mail,
      patientAPI.InterventionList
        ? InterventionList.factory(patientAPI.InterventionList)
        : undefined,
      patientAPI.TableauDeBord
        ? TableauDeBord.factory(patientAPI.TableauDeBord)
        : undefined,
      patientAPI.Telesuivi
        ? Telesuivi.factory(patientAPI.Telesuivi)
        : undefined,
      undefined,
      patientAPI.premierePrescriptionNonTransmise
        ? Prescription.factory(patientAPI.premierePrescriptionNonTransmise)
        : undefined,
      patientAPI.dernierePrescription
        ? Prescription.factory(patientAPI.dernierePrescription)
        : undefined,
      patientAPI.nombrePrescriptionEnCoursDeTransmission,
      patientAPI.dernierePrescriptionFormEnCoursDeTransmission
        ? PrescriptionForm.factory(
            patientAPI.dernierePrescriptionFormEnCoursDeTransmission,
          )
        : undefined,
      patientAPI.nombrePrescriptionEnAttente,
      patientAPI.CodeAssure,
      patientAPI.DetailTelesuivi
        ? DetailTelesuivi.factory(patientAPI.DetailTelesuivi)
        : undefined,
      patientAPI.activite
        ? patientAPI.activite.map(
            (activiteAPI: ActiviteAPI) => activiteAPI.ActivityCode,
          )
        : undefined,
      patientAPI.Assure ? Assure.factory(patientAPI.Assure) : undefined,
      patientAPI.MedecinList
        ? patientAPI.MedecinList.map((medecinAPI: UserAPI) =>
            User.factory(medecinAPI),
          )
        : undefined,
      patientAPI.CanSign,
      patientAPI.prescriptionFormPrecedente
        ? PrescriptionForm.factory(patientAPI.prescriptionFormPrecedente)
        : undefined,
      patientAPI.patient_complement
        ? PatientComplement.factory(patientAPI.patient_complement)
        : undefined,
    );
  }

  observanceMoyenne(dateDebut: Date, dateFin: Date): Observance {
    const observanceMoyenne = new Observance();

    let fuites: number = 0;
    let countFuites: number = 0;
    let iahResiduel: number = 0;
    let countIahResiduel: number = 0;
    let niveau: number = 0;
    let countNiveau: number = 0;
    let periode: number = 0;
    let countPeriode: number = 0;
    let pression: number = 0;
    let countPression: number = 0;
    let p90_95: number = 0;
    let countP90_95: number = 0;
    let seq: number = 0;
    let countSeq: number = 0;
    let obs: number = 0;
    let hasObsData = false;

    let dateDebutReel =
      this.patientComplement?.dateInstallation &&
      this.patientComplement.dateInstallation > dateDebut
        ? this.patientComplement.dateInstallation
        : dateDebut;

    this.telesuivi?.observanceList?.forEach((observance) => {
      if (
        observance.date &&
        observance.date >= dateDebut &&
        observance.date <= dateFin
      ) {
        if (observance.fuites != null) {
          fuites += observance.fuites;
          countFuites++;
        }
        if (observance.iahResiduel != null) {
          iahResiduel += observance.iahResiduel;
          countIahResiduel++;
        }
        if (observance.niveau != null) {
          niveau += observance.niveau;
          countNiveau++;
        }
        if (observance.periode != null) {
          periode += observance.periode;
          countPeriode++;
        }
        if (observance.p90_95 != null) {
          p90_95 += observance.p90_95;
          countP90_95++;
        }
        if (observance.pression != null) {
          pression += observance.pression;
          countPression++;
        }
        if (observance.seq != null) {
          seq += observance.seq;
          countSeq++;
        }
        if (observance.obs) {
          obs += observance.obs;
          hasObsData = true;
        }
      }
    });

    const countObs = Math.ceil(
      (dateFin.getTime() - dateDebutReel.getTime()) / (1000 * 60 * 60 * 24),
    );

    observanceMoyenne.fuites = countFuites ? fuites / countFuites : undefined;
    observanceMoyenne.iahResiduel = countIahResiduel
      ? iahResiduel / countIahResiduel
      : undefined;
    observanceMoyenne.niveau = countNiveau ? niveau / countNiveau : undefined;
    observanceMoyenne.periode = countPeriode
      ? periode / countPeriode
      : undefined;
    observanceMoyenne.pression = countPression
      ? pression / countPression
      : undefined;
    observanceMoyenne.p90_95 = countP90_95 ? p90_95 / countP90_95 : undefined;
    observanceMoyenne.seq = countSeq ? seq / countSeq : undefined;
    observanceMoyenne.obs = hasObsData ? obs / countObs : 0;

    return observanceMoyenne;
  }

  calculObs(observanceMoyenne?: Observance): ChampLibre | undefined {
    if (observanceMoyenne?.obs) {
      return new ChampLibre(
        'OBS',
        observanceMoyenne.obs ? observanceMoyenne.obs! : '',
        `Au ${environment.nombreDeJourPourMoyenne} derniers jours`,
      );
    }

    return this.tableauDeBord?.obs;
  }

  calculIahResiduel(observanceMoyenne?: Observance): ChampLibre | undefined {
    if (observanceMoyenne?.iahResiduel) {
      return new ChampLibre(
        'IAH RESIDUEL',
        observanceMoyenne.iahResiduel ?? '',
        `Au ${environment.nombreDeJourPourMoyenne} derniers jours`,
      );
    }

    return this.tableauDeBord?.iahResiduel;
  }

  calculFuites(observanceMoyenne?: Observance): ChampLibre | undefined {
    if (observanceMoyenne?.fuites) {
      return new ChampLibre(
        'FUITES',
        observanceMoyenne.fuites ?? '',
        `Au ${environment.nombreDeJourPourMoyenne} derniers jours`,
      );
    }

    return this.tableauDeBord?.fuites;
  }

  calculP90_95(observanceMoyenne?: Observance): ChampLibre | undefined {
    if (observanceMoyenne?.p90_95) {
      return new ChampLibre(
        'P90/95',
        observanceMoyenne.p90_95 ?? '',
        `Au ${environment.nombreDeJourPourMoyenne} derniers jours`,
      );
    }

    return this.tableauDeBord?.p90_95;
  }
}

export type PatientAPI = {
  Numero: number;
  Nom: string;
  Prenom: string;
  NomTitre: string;
  Sexe?: number;
  DateNaissance?: string;
  CodeAssure?: string;
  Adresse1?: string;
  Adresse2?: string;
  Adresse3?: string;
  CodePostal?: string;
  Ville?: string;
  Pays?: string;
  Telephone?: string;
  Mobile?: string;
  Mail?: string;
  InterventionList?: InterventionListAPI;
  TableauDeBord?: TableauDeBordAPI;
  Telesuivi?: TelesuiviAPI;
  premierePrescriptionNonTransmise?: PrescriptionAPI;
  dernierePrescription?: PrescriptionAPI;
  nombrePrescriptionEnAttente?: number;
  nombrePrescriptionEnCoursDeTransmission?: number;
  dernierePrescriptionFormEnCoursDeTransmission?: PrescriptionFormAPI;
  DetailTelesuivi?: DetailTelesuiviAPI;
  activite?: ActiviteAPI[];
  Assure?: AssureAPI;
  MedecinList?: UserAPI[];
  CanSign?: boolean;
  prescriptionFormPrecedente?: PrescriptionFormAPI;
  patient_complement?: PatientComplementAPI;
};

export class PatientList {
  data: Patient[];
  total: number;
  currentPage?: number;
  lastPage?: number;
  perPage?: number;
  from?: number;
  to?: number;

  constructor(
    data: Patient[],
    total: number,
    currentPage?: number,
    lastPage?: number,
    perPage?: number,
    from?: number,
    to?: number,
  ) {
    this.data = data;
    this.total = total;
    this.currentPage = currentPage;
    this.lastPage = lastPage;
    this.perPage = perPage;
    this.from = from;
    this.to = to;
  }

  static factory(patientListAPI: PatientListAPI): PatientList {
    return new PatientList(
      patientListAPI.data.map((dossier: PatientAPI) =>
        Patient.factory(dossier),
      ),
      patientListAPI.total,
      patientListAPI.current_page,
      patientListAPI.last_page,
      patientListAPI.per_page,
      patientListAPI.from,
      patientListAPI.to,
    );
  }
}

export type PatientListAPI = {
  data: PatientAPI[];
  total: number;
  current_page?: number;
  last_page?: number;
  per_page?: number;
  from?: number;
  to?: number;
};
